// src/pages/etudiant/Communaute.jsx
import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function Communaute() {
  const [groupes, setGroupes] = useState([]);
  const [selectedGroupe, setSelectedGroupe] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showGroupInfo, setShowGroupInfo] = useState(false);
  const [formData, setFormData] = useState({ nom: '', description: '', photo_groupe_base64: '', photo_preview: '' });
  const [inviteCode, setInviteCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showMediaModal, setShowMediaModal] = useState(null);
  
  // ✅ État pour l'enregistrement audio
  const [recording, setRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  
  const messagesEndRef = useRef(null);
  const messageInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const groupPhotoInputRef = useRef(null);
  const recordingTimerRef = useRef(null);
  const audioStreamRef = useRef(null);

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const MAX_SIZE = 40 * 1024 * 1024;
  const MAX_DURATION = 240; // 4 minutes max

  const normalizeGroup = (g) => ({
    ...g,
    photo_groupe: g?.photo_groupe
      ? config.normalizeUrl(g.photo_groupe)
      : null
  });

  const normalizeGroupMessage = (msg) => ({
    ...msg,
    url_fichier: msg?.url_fichier
      ? config.normalizeMessageMedia(msg.url_fichier)
      : null,
    photo_profil: msg?.photo_profil
      ? config.normalizeProfilePhoto(msg.photo_profil)
      : null
  });

  // Récupérer les groupes
  const fetchGroupes = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/communaute/get_groups.php`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setGroupes((data.groupes || []).map(normalizeGroup));
      }
    } catch (error) {
      console.error(error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Récupérer les messages d'un groupe
  const fetchMessages = async (id_groupe) => {
    try {
      const token = getToken();
      if (!token) return;
      
      const response = await fetch(`${API_BASE}/communaute/get_group_messages.php?id=${id_groupe}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        const msgs = (data.messages || []).map(normalizeGroupMessage);
        setMessages(msgs);
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    } catch (error) {
      console.error('Erreur fetchMessages:', error);
    }
  };

  // ✅ Démarrer l'enregistrement audio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      
      const recorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm'
      });
      
      const chunks = [];
      let startTime = Date.now();
      
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      
      recorder.onstop = async () => {
        const duration = Math.floor((Date.now() - startTime) / 1000);
        if (duration < 1) {
          toast.error('Audio trop court');
          stream.getTracks().forEach(t => t.stop());
          return;
        }
        
        const blob = new Blob(chunks, { type: 'audio/mp4' });
        const file = new File([blob], `audio_${Date.now()}.mp4`, { type: 'audio/mp4' });
        
        // Envoyer le fichier audio
        await sendFile(file, 'audio');
        stream.getTracks().forEach(t => t.stop());
        setRecordingTime(0);
      };
      
      recorder.start(1000);
      setMediaRecorder(recorder);
      setRecording(true);
      
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_DURATION) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (err) {
      toast.error('Microphone inaccessible');
    }
  };

  // ✅ Arrêter l'enregistrement audio
  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      setRecording(false);
      clearInterval(recordingTimerRef.current);
    }
  };

  // Formater le temps d'enregistrement
  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Créer un groupe
  const handleGroupPhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Choisissez une image pour la photo du groupe');
      e.target.value = '';
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('La photo du groupe ne doit pas d\u00e9passer 5 Mo');
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((previous) => ({
        ...previous,
        photo_groupe_base64: reader.result,
        photo_preview: reader.result
      }));
    };
    reader.onerror = () => toast.error('Impossible de lire la photo du groupe');
    reader.readAsDataURL(file);
  };
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!formData.nom.trim()) {
      toast.error('Le nom du groupe est requis');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/communaute/create_group.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Groupe créé !');
        setShowCreateModal(false);
        setFormData({ nom: '', description: '', photo_groupe_base64: '', photo_preview: '' });
        await fetchGroupes();
        toast.success(`Code d'invitation: ${data.code_invitation}`, { duration: 8000 });
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  // Rejoindre un groupe
  const handleJoinGroup = async () => {
    if (!inviteCode.trim()) {
      toast.error('Entrez un code d\'invitation');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/communaute/join_group.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code_invitation: inviteCode.toUpperCase() })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        setShowJoinModal(false);
        setInviteCode('');
        await fetchGroupes();
        if (data.id_groupe) {
          selectGroupe(data.id_groupe);
        }
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  // Envoyer un message
  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedGroupe) return;
    
    setSending(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/communaute/send_group_message.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          id_groupe: selectedGroupe.id_groupe,
          contenu: newMessage,
          type_message: 'texte'
        })
      });
      const data = await response.json();
      
      if (data.success) {
        setNewMessage('');
        await fetchMessages(selectedGroupe.id_groupe);
        await fetchGroupes();
      } else {
        toast.error(data.message || 'Erreur d\'envoi');
      }
    } catch (error) {
      console.error('Erreur sendMessage:', error);
      toast.error('Erreur d\'envoi');
    } finally {
      setSending(false);
    }
  };

  // Envoyer un fichier
  const sendFile = async (file, type) => {
    if (!selectedGroupe) return;
    if (file.size > MAX_SIZE) {
      toast.error(`Fichier trop volumineux (max 40 Mo)`);
      return;
    }
    
    setSending(true);
    const reader = new FileReader();
    reader.onloadend = async () => {
      try {
        const token = getToken();
        const response = await fetch(`${API_BASE}/communaute/send_group_message.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({
            id_groupe: selectedGroupe.id_groupe,
            contenu: '',
            type_message: type,
            base64_data: reader.result,
            file_name: file.name
          })
        });
        const data = await response.json();
        if (data.success) {
          await fetchMessages(selectedGroupe.id_groupe);
          await fetchGroupes();
          toast.success('Fichier envoyé');
        } else {
          toast.error(data.message || 'Erreur d\'envoi');
        }
      } catch (error) {
        console.error('Erreur sendFile:', error);
        toast.error('Erreur d\'envoi');
      } finally {
        setSending(false);
      }
    };
    reader.readAsDataURL(file);
  };

  // Gestion des fichiers
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.type.startsWith('image/')) {
      sendFile(file, 'image');
    } else if (file.type.startsWith('video/')) {
      sendFile(file, 'video');
    } else if (file.type === 'application/pdf') {
      sendFile(file, 'pdf');
    } else if (file.type.startsWith('audio/')) {
      sendFile(file, 'audio');
    } else {
      toast.error('Type de fichier non supporté');
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Sélectionner un groupe
  const selectGroupe = async (id_groupe) => {
    const groupe = groupes.find(g => g.id_groupe === id_groupe);
    if (groupe) {
      setSelectedGroupe(groupe);
      await fetchMessages(id_groupe);
    }
  };

  // Copier le code d'invitation
  const copyInviteCode = () => {
    if (selectedGroupe) {
      navigator.clipboard.writeText(selectedGroupe.code_invitation);
      setCopied(true);
      toast.success('Code copié !');
      setTimeout(() => setCopied(false), 3000);
    }
  };

  // Quitter un groupe
  const handleLeaveGroup = async () => {
    if (!selectedGroupe) return;
    if (!confirm(`Voulez-vous vraiment quitter "${selectedGroupe.nom}" ?`)) return;
    
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/communaute/leave_group.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ id_groupe: selectedGroupe.id_groupe })
      });
      const data = await response.json();
      if (data.success) {
        toast.success('Vous avez quitté le groupe');
        setSelectedGroupe(null);
        setMessages([]);
        fetchGroupes();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const openMediaModal = (url) => {
    setShowMediaModal(url);
  };

  useEffect(() => {
    fetchGroupes();
  }, []);

  // Nettoyer les ressources audio
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (audioStreamRef.current) {
        audioStreamRef.current.getTracks().forEach(t => t.stop());
      }
    };
  }, []);

  // Auto-rafraîchissement
  useEffect(() => {
    if (selectedGroupe) {
      const interval = setInterval(() => {
        fetchMessages(selectedGroupe.id_groupe);
        fetchGroupes();
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedGroupe]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[550px]">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-sm font-medium text-slate-500">Chargement des groupes...</p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-80px)] md:h-[calc(100vh-120px)] bg-slate-100 dark:bg-slate-950 rounded-3xl overflow-hidden shadow-xl border border-slate-200/50 dark:border-slate-800/50 relative antialiased">
      
      {/* SIDEBAR GROUPES */}
      <div className={`w-full lg:w-96 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800 flex flex-col transition-all duration-300 ${selectedGroupe ? 'hidden lg:flex' : 'flex'}`}>
        <div className="p-5 flex items-center justify-between border-b border-slate-100 dark:border-slate-800">
          <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">👥 Communauté</h2>
          <div className="flex gap-2">
            <button
              onClick={() => setShowJoinModal(true)}
              className="p-2.5 bg-slate-50 dark:bg-slate-800 text-blue-600 dark:text-blue-400 rounded-full hover:scale-105 active:scale-95 transition-all shadow-sm"
              title="Rejoindre un groupe"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="p-2.5 bg-slate-50 dark:bg-slate-800 text-emerald-600 dark:text-emerald-400 rounded-full hover:scale-105 active:scale-95 transition-all shadow-sm"
              title="Créer un groupe"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-0.5">
          {groupes.length === 0 ? (
            <div className="text-center py-20 text-slate-400">
              <p className="text-sm">Aucun groupe</p>
              <p className="text-xs">Créez ou rejoignez un groupe</p>
            </div>
          ) : (
            groupes.map(g => {
              const isActive = selectedGroupe?.id_groupe === g.id_groupe;
              return (
                <div
                  key={g.id_groupe}
                  onClick={() => selectGroupe(g.id_groupe)}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all duration-150 ${isActive ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800/40'}`}
                >
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-lg flex-shrink-0 overflow-hidden">
                    {g.photo_groupe ? (
                      <img src={g.photo_groupe} alt={g.nom} className="h-full w-full object-cover" />
                    ) : (
                      g.nom.charAt(0).toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-baseline mb-0.5">
                      <h3 className="font-semibold text-sm truncate">{g.nom}</h3>
                      <span className="text-[10px] text-slate-400">{g.nb_membres} 👤</span>
                    </div>
                    <p className={`text-xs truncate ${g.dernier_message ? 'text-slate-700 dark:text-slate-300' : 'text-slate-400'}`}>
                      {g.dernier_message || 'Aucun message'}
                    </p>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* ZONE DE CHAT DU GROUPE */}
      {selectedGroupe ? (
        <div className={`flex-1 flex flex-col w-full transition-all duration-300 bg-[#efeae2] dark:bg-[#0b141a] ${selectedGroupe ? 'flex' : 'hidden lg:flex'}`}>
          
          {/* Header du groupe */}
          <div className="p-3 bg-slate-200/90 dark:bg-[#202c33] backdrop-blur flex items-center justify-between sticky top-0 z-20 shadow-sm border-b border-slate-300/30 dark:border-slate-700/30">
            <div className="flex items-center gap-3">
              <button
                onClick={() => { setSelectedGroupe(null); setMessages([]); }}
                className="lg:hidden p-1.5 text-slate-600 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                {selectedGroupe.nom.charAt(0).toUpperCase()}
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">{selectedGroupe.nom}</h3>
                <p className="text-[10px] text-slate-500">
                  {selectedGroupe.nb_membres || 0} membres • {selectedGroupe.mon_role === 'admin' ? '👑 Admin' : 'Membre'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowGroupInfo(true)}
                className="p-2 text-slate-600 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition"
                title="Infos du groupe"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
              <button
                onClick={copyInviteCode}
                className="p-2 text-slate-600 dark:text-slate-300 hover:bg-black/5 dark:hover:bg-white/5 rounded-full transition"
                title="Copier le code d'invitation"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[url('https://user-images.githubusercontent.com/15075759/28719144-86dc0f70-73b1-11e7-911d-60d70fcded21.png')] bg-repeat opacity-[0.98]">
            {messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center">
                <span className="bg-white/80 dark:bg-[#182229] px-4 py-1.5 rounded-lg text-xs shadow-sm text-slate-500 dark:text-slate-400">
                  💬 Aucun message
                </span>
                <p className="text-[10px] text-slate-400 mt-2">Envoyez un message pour démarrer</p>
              </div>
            ) : (
              messages.map(msg => {
                const isMine = msg.id_expediteur === currentUser.id_utilisateur;
                return (
                  <div key={msg.id_message} className={`flex ${isMine ? 'justify-end' : 'justify-start'} items-end`}>
                    {!isMine && (
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 mr-2">
                        {msg.photo_profil ? (
                          <img src={msg.photo_profil} alt={msg.prenom} className="h-full w-full object-cover" />
                        ) : (
                          msg.prenom?.charAt(0).toUpperCase()
                        )}
                      </div>
                    )}
                    <div className={`max-w-[85%] md:max-w-[65%] rounded-xl px-3 py-1.5 shadow-sm relative group text-slate-900 dark:text-slate-100 ${
                      isMine 
                        ? 'bg-[#d9fdd3] dark:bg-[#005c4b] rounded-tr-none' 
                        : 'bg-white dark:bg-[#202c33] rounded-tl-none'
                    }`}>
                      {!isMine && (
                        <p className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 mb-0.5">
                          {msg.prenom} {msg.nom}
                        </p>
                      )}
                      
                      {/* Contenu texte */}
                      {msg.type_message === 'texte' && (
                        <div className="text-[14px] break-words whitespace-pre-wrap pr-12 leading-normal">
                          {msg.contenu || 'Message vide'}
                        </div>
                      )}

                      {/* Image */}
                      {msg.type_message === 'image' && msg.url_fichier && (
                        <div className="rounded-lg overflow-hidden my-0.5 max-w-sm border border-black/5 dark:border-white/5 bg-black/5 aspect-square max-h-64 sm:max-h-72">
                          <img 
                            src={msg.url_fichier} 
                            alt="Média" 
                            className="w-full h-full object-cover hover:opacity-95 transition cursor-pointer" 
                            onClick={() => openMediaModal(msg.url_fichier)} 
                          />
                        </div>
                      )}

                      {/* Vidéo */}
                      {msg.type_message === 'video' && msg.url_fichier && (
                        <div className="rounded-lg overflow-hidden my-0.5 max-w-sm border border-black/5 bg-black aspect-video max-h-64">
                          <video controls className="w-full h-full object-contain">
                            <source src={msg.url_fichier} type="video/mp4" />
                          </video>
                        </div>
                      )}

                      {/* PDF */}
                      {msg.type_message === 'pdf' && msg.url_fichier && (
                        <a href={msg.url_fichier} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-2.5 my-0.5 bg-black/5 dark:bg-black/20 rounded-lg hover:bg-black/10 transition border border-black/5 dark:border-white/5 max-w-sm">
                          <div className="p-2 bg-red-500 text-white rounded-lg text-xs font-bold">PDF</div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium truncate">{msg.file_name || 'Document.pdf'}</p>
                          </div>
                        </a>
                      )}

                      {/* Audio */}
                      {msg.type_message === 'audio' && msg.url_fichier && (
                        <div className="flex items-center gap-2 p-1 my-0.5 bg-black/5 dark:bg-black/20 rounded-lg min-w-[200px]">
                          <audio src={msg.url_fichier} controls className="w-full h-8" />
                        </div>
                      )}

                      {/* Date */}
                      <div className="absolute bottom-1 right-2 flex items-center gap-1 text-[10px] text-slate-400 select-none">
                        {formatDateTime(msg.date_envoi)}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* ✅ Saisie avec enregistrement audio (style WhatsApp) */}
          <div className="p-2 bg-slate-200/90 dark:bg-[#202c33] flex items-center gap-2 sticky bottom-0 z-20">
            <div className="flex items-center bg-white dark:bg-[#2a3942] rounded-full flex-1 px-3 py-1 shadow-sm border border-slate-300/20 dark:border-slate-700/20">
              <input
                ref={messageInputRef}
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') sendMessage(); }}
                placeholder={recording ? "Enregistrement audio..." : "Message dans le groupe..."}
                disabled={recording}
                className="w-full py-1.5 bg-transparent text-sm focus:outline-none placeholder:text-slate-400 dark:text-slate-100"
              />
            </div>
            
            <div className="relative flex items-center gap-2">
              {/* Bouton fichier */}
              <button
                onClick={() => fileInputRef.current?.click()}
                className="p-3 bg-slate-300 dark:bg-slate-600 text-slate-700 dark:text-slate-300 rounded-full hover:bg-slate-400 dark:hover:bg-slate-500 transition"
                title="Envoyer un fichier"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                </svg>
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,.pdf,audio/*"
                className="hidden"
                onChange={handleFileSelect}
              />

              {/* ✅ Bouton d'enregistrement audio (comme WhatsApp) */}
              {recording ? (
                <button
                  onClick={stopRecording}
                  className="p-3 bg-red-500 text-white rounded-full shadow-md flex items-center justify-center transition-transform animate-pulse"
                  title="Arrêter l'enregistrement"
                >
                  <span className="text-xs font-bold">
                    {formatRecordingTime(recordingTime)}s ⏹️
                  </span>
                </button>
              ) : (
                <button
                  onClick={startRecording}
                  className="p-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full shadow-md hover:shadow-lg transition-all hover:scale-105 active:scale-95"
                  title="Enregistrer un message audio"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </button>
              )}

              {/* Bouton envoyer */}
              <button
                onClick={sendMessage}
                disabled={sending || !newMessage.trim()}
                className="p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-md disabled:opacity-50 transition-transform active:scale-90"
              >
                <svg className="w-4 h-4 transform rotate-90 fill-current" viewBox="0 0 20 20">
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              </button>
            </div>
          </div>

        </div>
      ) : (
        <div className="hidden lg:flex flex-1 flex-col items-center justify-center bg-slate-50 dark:bg-[#222e35] text-slate-400">
          <p className="text-sm">Sélectionnez un groupe pour commencer</p>
        </div>
      )}

      {/* MODALS (inchangées) */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 box-border">
          <div className="bg-white dark:bg-[#222e35] rounded-2xl p-5 w-full max-w-sm shadow-2xl box-border border border-slate-200/20">
            <h3 className="text-base font-bold mb-3">📝 Créer un groupe</h3>
            <form onSubmit={handleCreateGroup} className="flex flex-col gap-3">
              <div className="flex justify-center">
                <button type="button" onClick={() => groupPhotoInputRef.current?.click()} className="h-20 w-20 rounded-full overflow-hidden bg-emerald-600 text-white text-xs font-bold">
                  {formData.photo_preview ? <img src={formData.photo_preview} alt="groupe" className="h-full w-full object-cover" /> : 'Photo'}
                </button>
                <input ref={groupPhotoInputRef} type="file" accept="image/*" className="hidden" onChange={handleGroupPhotoChange} />
              </div>
              <input
                type="text"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Nom du groupe *"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-[#2a3942] border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none dark:text-white"
                required
              />
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Description (optionnel)"
                rows="3"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-[#2a3942] border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none dark:text-white resize-none"
              />
              <div className="flex gap-2">
                <button type="submit" disabled={submitting} className="flex-1 py-2 bg-blue-600 text-white font-medium rounded-xl text-sm transition hover:bg-blue-700 disabled:opacity-50">
                  {submitting ? 'Création...' : 'Créer'}
                </button>
                <button type="button" onClick={() => setShowCreateModal(false)} className="flex-1 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium rounded-xl text-sm transition hover:bg-slate-300 dark:hover:bg-slate-600">
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showJoinModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 box-border">
          <div className="bg-white dark:bg-[#222e35] rounded-2xl p-5 w-full max-w-sm shadow-2xl box-border border border-slate-200/20">
            <h3 className="text-base font-bold mb-3">🔗 Rejoindre un groupe</h3>
            <p className="text-xs text-slate-400 mb-3">Entrez le code d'invitation partagé par un admin</p>
            <div className="flex flex-col gap-3">
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Code d'invitation (ex: ABC12345)"
                className="w-full px-3 py-2 bg-slate-50 dark:bg-[#2a3942] border border-slate-200 dark:border-slate-700 rounded-xl text-center text-sm font-mono uppercase focus:outline-none dark:text-white"
                maxLength={8}
              />
              <div className="flex gap-2">
                <button onClick={handleJoinGroup} disabled={submitting || !inviteCode.trim()} className="flex-1 py-2 bg-emerald-600 text-white font-medium rounded-xl text-sm transition hover:bg-emerald-700 disabled:opacity-50">
                  {submitting ? 'Connexion...' : 'Rejoindre'}
                </button>
                <button onClick={() => setShowJoinModal(false)} className="flex-1 py-2 bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 font-medium rounded-xl text-sm transition hover:bg-slate-300 dark:hover:bg-slate-600">
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGroupInfo && selectedGroupe && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 box-border">
          <div className="bg-white dark:bg-[#222e35] rounded-2xl p-5 w-full max-w-sm shadow-2xl box-border border border-slate-200/20">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-bold">ℹ️ Infos du groupe</h3>
              <button onClick={() => setShowGroupInfo(false)} className="p-1 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition">✕</button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold text-xl">
                  {selectedGroupe.nom.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h4 className="font-bold">{selectedGroupe.nom}</h4>
                  <p className="text-xs text-slate-500">{selectedGroupe.nb_membres || 0} membres</p>
                </div>
              </div>
              {selectedGroupe.description && (
                <p className="text-sm text-slate-600 dark:text-slate-400 border-t border-slate-100 dark:border-slate-700 pt-3">{selectedGroupe.description}</p>
              )}
              <div className="border-t border-slate-100 dark:border-slate-700 pt-3">
                <p className="text-xs text-slate-500">👑 Créé par {selectedGroupe.createur_prenom} {selectedGroupe.createur_nom}</p>
                <div className="flex items-center gap-2 mt-2 p-2 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <code className="text-sm font-mono flex-1">{selectedGroupe.code_invitation}</code>
                  <button onClick={copyInviteCode} className="px-3 py-1 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
                    {copied ? '✅ Copié' : '📋 Copier'}
                  </button>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">Code d'invitation à partager</p>
              </div>
              <button onClick={handleLeaveGroup} className="w-full py-2 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20 transition font-medium">
                Quitter le groupe
              </button>
            </div>
          </div>
        </div>
      )}

      {showMediaModal && (
        <div 
          className="fixed inset-0 bg-black/95 z-[9999] flex flex-col items-center justify-center p-4 box-border touch-none select-none"
          onClick={() => setShowMediaModal(null)}
        >
          <div className="w-full max-w-full max-h-full flex items-center justify-center relative box-border">
            <img 
              src={showMediaModal} 
              alt="HD" 
              className="max-w-full max-h-[82vh] md:max-h-[88vh] object-contain rounded shadow-2xl animate-in zoom-in-95 duration-150 pointer-events-auto"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <span className="text-white/60 text-[11px] mt-4 tracking-wide bg-white/10 px-3 py-1.5 rounded-full select-none">
            Fermer l'aperçu
          </span>
        </div>
      )}

    </div>
  );
}